import React, { useEffect, useState } from "react";
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import Button from "../../../ui/Button";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import {
  fetchKycSettings,
  updateKycSettingsThunk,
} from "../../../../redux/features/settingsSlice";
import CurrencyInput from "../../../ui/CurrencyInput";
import Dropdown from "../../../ui/SelectDropdown";

interface EditKycSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

const EditKycSettingsModal: React.FC<EditKycSettingsModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { kyc, loading } = useAppSelector((state) => state.settings);

  const [selectedKycStatus, setSelectedKycStatus] = useState<string>("");
  const [form, setForm] = useState({
    valueCap: "",
    kycFee: "",
    dailyWithdrawalLimit: "",
    monthlyWithdrawalLimit: "",
  });

  const kycOptions = kyc?.map((item: any) => ({
    label: item.kycStatus,
    value: item.kycStatus,
  }));

  // Set form values when kyc or selected status changes
  useEffect(() => {
    const selected = kyc?.find((k: any) => k.kycStatus === selectedKycStatus);
    if (selected) {
      setForm({
        valueCap: selected.valueCap?.toLocaleString() || "0",
        kycFee: selected.kycFee?.toLocaleString() || "0",
        dailyWithdrawalLimit:
          selected.dailyWithdrawalLimit?.toLocaleString() || "0",
        monthlyWithdrawalLimit:
          selected.monthlyWithdrawalLimit?.toLocaleString() || "0",
      });
    }
  }, [kyc, selectedKycStatus]);

  const handleChange = (field: string, val: string) => {
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const handleSubmit = async () => {
    const updatedKyc = kyc?.map((item: any) => {
      if (item.kycStatus === selectedKycStatus) {
        return {
          ...item,
          valueCap: parseInt(form.valueCap.replace(/,/g, "")) || 0,
          kycFee: parseInt(form.kycFee.replace(/,/g, "")) || 0,
          dailyWithdrawalLimit:
            parseInt(form.dailyWithdrawalLimit.replace(/,/g, "")) || 0,
          monthlyWithdrawalLimit:
            parseInt(form.monthlyWithdrawalLimit.replace(/,/g, "")) || 0,
        };
      }
    });

    await dispatch(
      updateKycSettingsThunk(updatedKyc.filter(Boolean)[0])
    ).unwrap();
    toast.success("KYC settings updated");
    dispatch(fetchKycSettings());
    onClose();
  };

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Edit KYC Settings"
      open={open}
      onClose={onClose}
      footer={
        <div className="flex justify-between">
          <Button
            label="Discard"
            onClick={onClose}
            layout="inline"
            variant="ghost"
          />
          <Button
            label="Done"
            onClick={handleSubmit}
            isLoading={loading}
            layout="inline"
          />
        </div>
      }
    >
      <div className="p-6">
        <Dropdown
          label="KYC Type"
          options={kycOptions}
          value={selectedKycStatus}
          onSelect={(val) => setSelectedKycStatus(val)}
          placeholder="Select KYC type"
          required
        />

        {selectedKycStatus && (
          <>
            <CurrencyInput
              label="Value Cap"
              value={form.valueCap}
              onChange={(val) => handleChange("valueCap", val)}
            />
            <CurrencyInput
              label="KYC Fee"
              value={form.kycFee}
              onChange={(val) => handleChange("kycFee", val)}
            />
            <CurrencyInput
              label="Daily Withdrawal Limit"
              value={form.dailyWithdrawalLimit}
              onChange={(val) => handleChange("dailyWithdrawalLimit", val)}
            />
            <CurrencyInput
              label="Monthly Withdrawal Limit"
              value={form.monthlyWithdrawalLimit}
              onChange={(val) => handleChange("monthlyWithdrawalLimit", val)}
            />
          </>
        )}
      </div>
    </CustomModalContainer>
  );
};

export default EditKycSettingsModal;
